var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable react/require-default-props */
import React, { useCallback, useState, useMemo, useEffect, useRef } from 'react';
import { translate } from '@utils/i18n';
import { Button, DraggableTreeView } from '@ui';
import ConfirmDialog from '@components/ConfirmDialog/ConfirmDialog';
import { useComponents } from './hooks/useComponentsQuery';
import useFilteredComponents from './hooks/useFilteredComponents';
import { mapComponentsToGroupedTreeItems, mapAttachableToParentOption, buildReorderResultWithAttachableUpdates } from './mapper';
import ComponentTreeItem from './components/ComponentTreeItem';
import ComponentsFilterPanel from './components/ComponentsFilterPanel';
import AddComponentModal from './components/AddComponentModal';
import ComponentDetailModal from './components/ComponentDetailModal';
import ConfigurationActions from './components/ConfigurationActions';
import './ComponentsTabStyles.scss';
import isComponent from './utils/isComponent';
import { buildAddComponentLaunchContext, findComponentRecursively } from './utils/addComponentLaunchContext';
import { componentHasCompatibleChildType } from './utils/componentCompatibility';
import { visibleAttachablesForTypeFilter } from './utils/filteredParentVisibility';
const namespace = 'components';
const tabNamespace = 'components.tab';
const treeNamespace = 'components.tree_item_actions';
const NO_VALID_CHILD_TYPES_MESSAGE = 'This component type does not allow any child component types.';
const EMPTY_LAUNCH_CONTEXT = { lockedParent: null, lockedTypeId: null };
export default function ComponentsTab({ buildingId = null, attachableId = null, attachableType = null, supportsSubcomponents = false, hasComponentsV2 = false, canAccessSettings = false, canManageComponents = false, initialTypeIds = [], openAddOnLoad = false }) {
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [addComponentContext, setAddComponentContext] = useState(EMPTY_LAUNCH_CONTEXT);
    const [toggleActiveConfirmModalOpen, setToggleActiveConfirmModalOpen] = useState(false);
    const [componentToToggle, setComponentToToggle] = useState(null);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [selectedComponent, setSelectedComponent] = useState(null);
    // Build filter params - use either buildingId or attachableId
    const filterParams = {
        buildingId,
        attachableId,
        attachableType
    };
    const { components, componentTypes, componentAttachables, parentComponentAttachables, isLoading, createComponent, isCreatingComponent, updateComponent, isUpdatingComponent, toggleActiveComponent, isTogglingActive, reorderComponents } = useComponents(filterParams);
    const componentTypesById = useMemo(() => {
        return new Map(componentTypes.map((componentType) => [componentType.id, componentType]));
    }, [componentTypes]);
    const hasValidChildType = useCallback((component) => {
        const parentType = componentTypesById.get(component.typeId);
        return componentHasCompatibleChildType(parentType, component.attachableType, componentTypesById);
    }, [componentTypesById]);
    const annotateDisabledAddButtons = useCallback((items) => {
        return items.map((item) => {
            const children = item.children ? annotateDisabledAddButtons(item.children) : item.children;
            if (!isComponent(item.data)) {
                return Object.assign(Object.assign({}, item), { children });
            }
            return Object.assign(Object.assign({}, item), { children, isDragDisabled: !canManageComponents, addDisabledReason: hasValidChildType(item.data)
                    ? undefined
                    : translate('no_valid_child_types', {
                        namespace: treeNamespace,
                        defaultValue: NO_VALID_CHILD_TYPES_MESSAGE,
                        default: NO_VALID_CHILD_TYPES_MESSAGE
                    }) });
        });
    }, [hasValidChildType, canManageComponents]);
    // Use filtered components hook for filter state and logic
    const { filters, setFilters, filteredComponents, onResetCache } = useFilteredComponents({
        components,
        componentTypes,
        buildingId,
        attachableId,
        initialTypeIds
    });
    // With a type filter, also render empty attachables the filtered type is allowed to use.
    const visibleAttachables = useMemo(() => {
        return visibleAttachablesForTypeFilter({
            typeIds: filters.typeIds,
            buildingLevel: attachableId == null,
            componentAttachables,
            parentComponentAttachables,
            componentTypes,
            filteredComponents
        });
    }, [
        filters.typeIds,
        attachableId,
        componentAttachables,
        parentComponentAttachables,
        componentTypes,
        filteredComponents
    ]);
    // Convert to tree items for DraggableTreeView (grouped by attachableId)
    const treeItems = useMemo(() => {
        return annotateDisabledAddButtons(mapComponentsToGroupedTreeItems(filteredComponents, visibleAttachables));
    }, [filteredComponents, visibleAttachables, annotateDisabledAddButtons]);
    const shouldShowFilteredEmptyState = filteredComponents.length === 0 &&
        (filters.typeIds.length === 0 || visibleAttachables.length === 0) &&
        components.length > 0 &&
        attachableId == null;
    const handleToggleActiveComponent = (componentId) => {
        const component = findComponentRecursively(components, componentId);
        if (component) {
            setComponentToToggle(component);
            setToggleActiveConfirmModalOpen(true);
        }
    };
    const confirmToggleActive = () => {
        if (componentToToggle) {
            void toggleActiveComponent(componentToToggle.id);
        }
        setToggleActiveConfirmModalOpen(false);
        setComponentToToggle(null);
    };
    const cancelToggleActive = () => {
        setToggleActiveConfirmModalOpen(false);
        setComponentToToggle(null);
    };
    const handleReorderComponents = (result) => {
        // Only allow reordering for component items, not attachable root items
        const draggedItemId = String(result.draggedItem.id);
        if (draggedItemId.startsWith('attachable-')) {
            return;
        }
        // Builds attachable_id/attachable_type updates for the dragged component and its
        // descendants when the drop target's attachable context differs from the dragged
        // item's current one (including deriving the "Component" marker for a Component target).
        const uiReorderResult = buildReorderResultWithAttachableUpdates(result, components);
        reorderComponents(uiReorderResult);
    };
    // When there is exactly one URL-seeded type filter, it is considered the "locked type" context
    // for attachable-root adds, or standalone add when the tab is scoped to a single attachable.
    const lockedTypeFromUrl = initialTypeIds.length === 1 ? initialTypeIds[0] : null;
    const handleAddItem = (item) => {
        const context = buildAddComponentLaunchContext({
            item,
            components,
            componentAttachables,
            lockedTypeFromUrl
        });
        setAddComponentContext(context);
        setIsAddModalOpen(true);
    };
    const handleStandaloneAdd = () => {
        // When the tab is scoped to a single attachable (attachableId set) AND there is exactly one
        // URL-seeded type filter, lock both parent and type per the breakdown-link add context rule.
        if (attachableId != null && attachableType != null && lockedTypeFromUrl != null) {
            const matchedAttachable = componentAttachables.find((a) => a.attachableId === attachableId && a.attachableType === attachableType);
            if (matchedAttachable) {
                setAddComponentContext({
                    lockedParent: mapAttachableToParentOption(matchedAttachable),
                    lockedTypeId: lockedTypeFromUrl
                });
                setIsAddModalOpen(true);
                return;
            }
        }
        // Default: fully free-form add (parent is searchable, type is editable)
        setAddComponentContext(EMPTY_LAUNCH_CONTEXT);
        setIsAddModalOpen(true);
    };
    const handleCloseAddModal = () => {
        setIsAddModalOpen(false);
        setAddComponentContext(EMPTY_LAUNCH_CONTEXT);
    };
    const hasAutoOpenedAddRef = useRef(false);
    useEffect(() => {
        if (!canManageComponents || !openAddOnLoad || isLoading || hasAutoOpenedAddRef.current)
            return;
        hasAutoOpenedAddRef.current = true;
        handleStandaloneAdd();
    }, [canManageComponents, openAddOnLoad, isLoading, handleStandaloneAdd]);
    // Helper function to recursively find component by ID in all components including children
    // Helper function to recursively collect all components from the hierarchy
    const getAllComponentsFlat = (componentsArray) => {
        const allComponents = [];
        componentsArray.forEach((component) => {
            allComponents.push(component);
            if (component.children && component.children.length > 0) {
                allComponents.push(...getAllComponentsFlat(component.children));
            }
        });
        return allComponents;
    };
    const handleComponentClick = (componentId) => {
        // Find the full component object from the components array including children
        const fullComponent = findComponentRecursively(components, componentId);
        if (fullComponent) {
            setSelectedComponent(fullComponent);
            setIsDetailModalOpen(true);
        }
    };
    const handleCloseDetailModal = () => {
        setIsDetailModalOpen(false);
        setSelectedComponent(null);
    };
    const handleUpdateComponent = (componentId, field, value) => __awaiter(this, void 0, void 0, function* () {
        try {
            yield updateComponent(componentId, field, value);
        }
        catch (err) {
            console.error('Failed to update component:', err);
            // Error handling is already handled by the React Query mutation via toast
            throw err;
        }
    });
    const handleToggleActiveFromModal = (componentId) => {
        // Use the existing toggle logic
        setIsDetailModalOpen(false);
        setSelectedComponent(null);
        handleToggleActiveComponent(componentId);
        return Promise.resolve();
    };
    const handleMoveComponent = (componentId, direction) => {
        const component = findComponentRecursively(components, componentId);
        if (!component)
            return;
        // Find siblings with same parent from all components in the hierarchy
        const allComponents = getAllComponentsFlat(components);
        const siblings = allComponents.filter((c) => c.attachableId === component.attachableId && c.parentId === component.parentId);
        // Sort by position
        siblings.sort((a, b) => a.position - b.position);
        const currentIndex = siblings.findIndex((c) => c.id === componentId);
        if (currentIndex === -1)
            return;
        let newIndex;
        if (direction === 'up' && currentIndex > 0) {
            newIndex = currentIndex - 1;
        }
        else if (direction === 'down' && currentIndex < siblings.length - 1) {
            newIndex = currentIndex + 1;
        }
        else {
            return; // Can't move in that direction
        }
        // Calculate position updates for reordering
        const positionUpdates = [];
        // Move the component being moved to its new position
        positionUpdates.push({
            id: componentId,
            position: newIndex,
            parentId: component.parentId || null
        });
        // Update positions of affected siblings
        siblings.forEach((sibling, index) => {
            if (sibling.id === componentId)
                return; // Skip the moved component
            let newPosition = index;
            // Adjust positions based on the move direction
            if (direction === 'up') {
                // Moving component up: shift the component that was above down
                if (index === currentIndex - 1) {
                    newPosition = currentIndex; // The component that was above moves down
                }
            }
            else if (direction === 'down') {
                // Moving component down: shift the component that was below up
                if (index === currentIndex + 1) {
                    newPosition = currentIndex; // The component that was below moves up
                }
            }
            // Only add update if position actually changed
            if (newPosition !== sibling.position) {
                positionUpdates.push({
                    id: sibling.id,
                    position: newPosition,
                    parentId: sibling.parentId || null
                });
            }
        });
        // Create UIReorderResult and use reorder API
        const uiReorderResult = {
            draggedItem: {
                id: componentId,
                parentId: component.parentId || null,
                position: component.position
            },
            newParentId: component.parentId || null, // Same parent for up/down moves
            newPosition: newIndex,
            positionUpdates
        };
        // Use reorder components instead of individual update
        reorderComponents(uiReorderResult);
    };
    const handleMoveToComponent = (componentId, newParentId) => {
        // Move component to be a child of another component
        void updateComponent(componentId, 'parent_component_id', newParentId);
    };
    const handleMakeMainComponent = (componentId) => {
        // Make component a main component (remove parent, make direct child of attachable)
        void updateComponent(componentId, 'parent_component_id', null);
    };
    // Stable itemRenderer — memoised so DraggableTreeView's memoised nodes
    // don't re-render on every ComponentsTab render. Replaces the previous
    // inline renderer that required `eslint-disable react/no-unstable-nested-components`.
    const renderComponentItem = useCallback((item, options, dragHandle) => {
        const isAttachableRoot = !isComponent(item.data);
        return (React.createElement(ComponentTreeItem, { item: item, options: options, onToggleActive: () => handleToggleActiveComponent(Number(item.id)), isTogglingActive: isTogglingActive, hideActions: isAttachableRoot || !canManageComponents, onComponentClick: canManageComponents ? handleComponentClick : undefined, components: components, onEdit: handleComponentClick, onMove: handleMoveComponent, onMoveTo: handleMoveToComponent, onMakeMainComponent: handleMakeMainComponent, dragHandle: dragHandle, supportsSubcomponents: supportsSubcomponents }));
    }, [
        isTogglingActive,
        components,
        supportsSubcomponents,
        canManageComponents
        // handleToggleActiveComponent / handleComponentClick / handleMove* are
        // stable function refs declared in the outer scope — they close over
        // state through `components` which is already a dep. Listing them here
        // would fight React's exhaustive-deps without changing behavior.
    ]);
    if (isLoading) {
        return (React.createElement("div", { className: "components-tab" },
            React.createElement("div", { className: "components-tab__loading" },
                React.createElement("div", { className: "loading-spinner" }),
                React.createElement("span", null, translate('loading', { namespace: tabNamespace })))));
    }
    return (React.createElement("div", { className: "components-tab" },
        React.createElement("div", { className: "components-tab__header" },
            React.createElement(ComponentsFilterPanel, { filters: filters, onFiltersChange: setFilters, componentTypes: componentTypes, onResetCache: onResetCache }),
            React.createElement("div", { className: "components-tab__actions" },
                hasComponentsV2 && canAccessSettings && React.createElement(ConfigurationActions, null),
                canManageComponents && (React.createElement(Button, { variant: "primary", onClick: handleStandaloneAdd, className: "add-component-btn" },
                    React.createElement("i", { className: "fa-light fa-plus" }),
                    translate('add_component', { namespace: tabNamespace }))))),
        React.createElement("div", { className: "components-tab__content" }, shouldShowFilteredEmptyState ? (React.createElement("div", { className: "components-tab__empty" },
            React.createElement("div", { className: "empty-state" },
                React.createElement("h3", null, translate('filtered_empty_title', { namespace: tabNamespace })),
                React.createElement("p", null, translate('filtered_empty_description', { namespace: tabNamespace })),
                React.createElement(Button, { variant: "secondary", onClick: () => setFilters({ search: '', typeIds: [], activeState: 'active' }) }, translate('clear_filters', { namespace: tabNamespace }))))) : (React.createElement("div", { className: "components-tab__tree" },
            treeItems.length > 0 && (React.createElement(DraggableTreeView, { items: treeItems, onDragEnd: handleReorderComponents, onAddItem: handleAddItem, pluralKey: translate('plural_key', { namespace: tabNamespace }), showPlusButton: canManageComponents, maxDepth: supportsSubcomponents ? 5 : 2, itemRenderer: renderComponentItem, className: "components-tree" })),
            components.length === 0 && treeItems.length === 0 && (React.createElement("div", { className: "components-tab__empty" },
                React.createElement("div", { className: "empty-state" },
                    React.createElement("p", null, translate(buildingId ? 'empty_building' : 'empty_device', { namespace: tabNamespace })))))))),
        (isTogglingActive || isUpdatingComponent) && (React.createElement("div", { className: "components-tab__loading-overlay" },
            React.createElement("div", { className: "loading-spinner" }))),
        React.createElement(AddComponentModal, { isOpen: isAddModalOpen, onClose: handleCloseAddModal, components: components, componentTypes: componentTypes, componentAttachables: parentComponentAttachables, createComponent: createComponent, isCreating: isCreatingComponent, lockedParent: addComponentContext.lockedParent, lockedTypeId: addComponentContext.lockedTypeId, supportsSubcomponents: supportsSubcomponents }),
        toggleActiveConfirmModalOpen && componentToToggle && (React.createElement(ConfirmDialog, { title: componentToToggle.active
                ? translate('deactivate_component_title', { namespace })
                : translate('activate_component_title', { namespace }), description: componentToToggle.active
                ? translate(supportsSubcomponents && componentToToggle.children && componentToToggle.children.length > 0
                    ? 'deactivate_component_description'
                    : 'deactivate_component_description_no_children', { namespace })
                : translate(supportsSubcomponents && componentToToggle.children && componentToToggle.children.length > 0
                    ? 'activate_component_description'
                    : 'activate_component_description_no_children', { namespace }), onConfirm: confirmToggleActive, onCancel: cancelToggleActive, yesLabel: componentToToggle.active ? translate('deactivate', { namespace }) : translate('activate', { namespace }), noLabel: translate('cancel', { namespace }) })),
        React.createElement(ComponentDetailModal, { isOpen: isDetailModalOpen, onClose: handleCloseDetailModal, component: getAllComponentsFlat(components).find((c) => c.id === (selectedComponent === null || selectedComponent === void 0 ? void 0 : selectedComponent.id)) || null, components: components, componentTypes: componentTypes, onUpdate: handleUpdateComponent, onToggleActive: handleToggleActiveFromModal, isTogglingActive: isTogglingActive })));
}
